import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { MultiValue } from 'react-select';

import { SourceWhaleTrackingEvents, userTracking } from '@revelio/core';
import type { TalentDiscoveryV2RespUser } from '@revelio/data-access';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  Label,
  Select,
} from '@revelio/ui';

import sourceWhaleIcon from '../../../../assets/sourcewhale-icon.png';
import themedModalStyles from '../../../Modal/themedModal.module.css';
import {
  CannotFetchCampaignsError,
  useSourceWhaleCampaigns,
} from '../hooks/use-source-whale-campaigns';
import { useSourceWhaleCandidates } from '../hooks/use-source-whale-candidates';
import {
  CannotFetchProjectsError,
  useSourceWhaleProjects,
} from '../hooks/use-source-whale-projects';
import { mapTDUsersToSWCandidates } from '../source-whale.model';
import { SelectableCard } from './selectable-card';
import {
  SourceWhaleCampaignsIcon,
  SourceWhaleContactsIcon,
} from './source-whale-export-icons';
import styles from './source-whale-modals.module.css';

const MAX_CAMPAIGN_CANDIDATES = 50;
const NO_CANDIDATES_SELECTED_HINT = 'Select at least one candidate to export';

enum ExportMode {
  Contacts = 'contacts',
  Campaign = 'campaign',
}

type Option = {
  label: string;
  value: string;
};

interface SourceWhaleExportModalProps {
  apiKey: string;
  isOpen: boolean;
  selectedUsers: TalentDiscoveryV2RespUser[];
  onClose: () => void;
  onEditApiKey: () => void;
}

export const SourceWhaleExportModal = ({
  apiKey,
  isOpen,
  onClose,
  selectedUsers,
  onEditApiKey,
}: SourceWhaleExportModalProps) => {
  const isCampaignDisabled = selectedUsers.length > MAX_CAMPAIGN_CANDIDATES;

  const [exportMode, setExportMode] = useState<ExportMode | null>(
    isCampaignDisabled ? ExportMode.Contacts : null
  );
  const [campaign, setCampaign] = useState<Option | null>(null);
  const [error, setError] = useState<string>('');

  const { data, isLoading, isError } = useSourceWhaleCampaigns({
    apiKey,
    onError: (error) => setError(error),
    enabled: isOpen,
  });

  const { data: projectsData, isLoading: isLoadingProjects } =
    useSourceWhaleProjects({
      apiKey,
      onError: (error) => setError(error),
      enabled: isOpen,
    });

  const { mutate: addCandidates, isPending: isAddingCandidates } =
    useSourceWhaleCandidates({
      onSuccess: () => {
        handleClose();
      },
      onError: (error) => {
        setError(error);
      },
    });

  const campaignOptions = useMemo(() => {
    if (isError || !data) return [];

    return (
      data?.campaigns.campaigns.map((campaign) => ({
        label: campaign.campaignName,
        value: campaign.campaignId,
      })) ?? []
    );
  }, [data, isError]);

  const projectSelectContainerRef = useRef<HTMLDivElement>(null);
  const [isSyncWithCrmSelected, setIsSyncWithCrmSelected] = useState(false);

  const handleSyncWithCrmToggle = () => {
    const next = !isSyncWithCrmSelected;
    setIsSyncWithCrmSelected(next);
    if (next) {
      // Focus the project select once it re-renders enabled; with
      // `openMenuOnFocus` this also opens its menu.
      requestAnimationFrame(() => {
        projectSelectContainerRef.current?.querySelector('input')?.focus();
      });
    }
  };

  const [projects, setProjects] = useState<MultiValue<Option> | null>(null);
  const projectOptions =
    projectsData?.projects?.map((project) => ({
      label: project.projectName,
      value: project.projectId,
    })) ?? [];

  const projectIds = (() => {
    if (!projects || projects.length === 0) return undefined;
    return projects.map((project) => project.value);
  })();

  const noCandidatesSelected = selectedUsers.length === 0;
  const alertMessage = (() => {
    if (error) return error;
    if (noCandidatesSelected) return NO_CANDIDATES_SELECTED_HINT;
    return '';
  })();

  const isSubmitDisabled =
    noCandidatesSelected ||
    !exportMode ||
    (exportMode === ExportMode.Campaign && !campaign);

  const handleSubmit = useCallback(() => {
    if (!apiKey || selectedUsers.length === 0) return;

    if (exportMode === ExportMode.Campaign) {
      userTracking.track(SourceWhaleTrackingEvents.EXPORT_CAMPAIGN, {
        candidatesCount: selectedUsers.length,
      });
    } else if (exportMode === ExportMode.Contacts) {
      userTracking.track(SourceWhaleTrackingEvents.EXPORT_CONTACTS, {
        contactsCount: selectedUsers.length,
        syncWithCrm: isSyncWithCrmSelected,
        projectsCount: projects?.length || 0,
      });
    }

    addCandidates({
      apiKey,
      candidates: mapTDUsersToSWCandidates({ users: selectedUsers }),
      campaignId:
        exportMode === ExportMode.Campaign ? campaign?.value : undefined,
      projectIds:
        exportMode === ExportMode.Contacts && isSyncWithCrmSelected
          ? projectIds
          : undefined,
    });
  }, [
    apiKey,
    addCandidates,
    selectedUsers,
    exportMode,
    campaign,
    projectIds,
    isSyncWithCrmSelected,
    projects,
  ]);

  const handleReset = useCallback(() => {
    setError('');
    setExportMode(isCampaignDisabled ? ExportMode.Contacts : null);
    setCampaign(null);
  }, [isCampaignDisabled]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      data-testid="source-whale-export-modal"
      title={
        <span className={styles.headerTitle}>
          <img src={sourceWhaleIcon} alt="" className={styles.headerIcon} />
          SourceWhale Export
        </span>
      }
      size="sm"
      className={classNames(themedModalStyles.content, styles.modalContent)}
      footer={
        <ButtonGroup size="sm" className={styles.footerButtons}>
          <Button variant="link" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            loading={isAddingCandidates}
            data-testid="Export-submit"
          >
            Export
          </Button>
        </ButtonGroup>
      }
    >
      {alertMessage && (
        <ExportErrorAlert
          error={alertMessage}
          onEditApiKey={onEditApiKey}
          onClose={handleClose}
        />
      )}

      <Label as="p" size="sm" className={styles.sectionLabel}>
        Where would you like to export to?
      </Label>

      <div className={styles.cardList}>
        <SelectableCard
          icon={<SourceWhaleContactsIcon />}
          label="Add to contacts"
          isSelected={exportMode === ExportMode.Contacts}
          onClick={() => setExportMode(ExportMode.Contacts)}
        />
        {exportMode === ExportMode.Contacts && (
          <div className={styles.syncSection}>
            <Checkbox
              className={styles.checkboxRow}
              checked={isSyncWithCrmSelected}
              onChange={handleSyncWithCrmToggle}
            >
              <Label as="span" size="sm">
                Sync with CRM & ATS?
              </Label>
            </Checkbox>

            <div ref={projectSelectContainerRef}>
              <Select
                isMulti
                isDisabled={!isSyncWithCrmSelected}
                options={projectOptions}
                hideSelectedOptions={false}
                value={projects}
                onChange={(options) => setProjects(options)}
                placeholder="Select CRM & ATS"
                isLoading={isLoadingProjects}
                size="sm"
                isClearable={false}
                closeMenuOnSelect={false}
                openMenuOnFocus
              />
            </div>
          </div>
        )}
        <SelectableCard
          icon={<SourceWhaleCampaignsIcon />}
          label="Add to a campaign"
          tooltip="Select up to 50 candidates to add to your campaign"
          isSelected={exportMode === ExportMode.Campaign}
          isDisabled={isCampaignDisabled}
          onClick={() =>
            !isCampaignDisabled && setExportMode(ExportMode.Campaign)
          }
        />
      </div>

      {exportMode === ExportMode.Campaign && (
        <>
          <Label as="p" size="sm" className={styles.campaignLabel}>
            Choose a campaign
          </Label>

          <Select<Option>
            options={campaignOptions}
            value={campaign}
            onChange={(option) => setCampaign(option)}
            placeholder="Select a campaign"
            isLoading={isLoading}
            size="sm"
          />
        </>
      )}
    </Dialog>
  );
};

const ExportErrorAlert = ({
  error,
  onEditApiKey,
  onClose,
}: {
  error: string;
  onEditApiKey: () => void;
  onClose: () => void;
}) => {
  const renderErrorRecoveryAction = () => {
    // Failing to fetch campaigns or projects often indicate an invalid API key.
    // Provide a direct way for users to update their key.
    if (
      error === CannotFetchCampaignsError ||
      error === CannotFetchProjectsError
    ) {
      return (
        <>
          {' '}
          Please{' '}
          <button
            type="button"
            className={styles.alertLink}
            onClick={() => {
              onClose();
              onEditApiKey();
            }}
          >
            check your API key
          </button>{' '}
          and try again.
        </>
      );
    }
    return null;
  };

  return (
    <div className={styles.alert} role="alert">
      <span>
        {error}
        {renderErrorRecoveryAction()}
      </span>
    </div>
  );
};
